import { PackageEvidence } from '@cadstrom/cadstrom-shared';

/** How a tier's voters are named: for one, with its article, and for several. */
interface TierVoterNames {
	one: string;
	several: string;
}

const TIER_VOTER_NAMES: Readonly<Record<PackageEvidence.EvidenceVoterTier, TierVoterNames>> = {
	[PackageEvidence.EvidenceVoterTier.AdminReviewer]: { one: 'a staff reviewer', several: 'staff reviewers' },
	[PackageEvidence.EvidenceVoterTier.ExpertReviewer]: { one: 'an expert reviewer', several: 'expert reviewers' },
	[PackageEvidence.EvidenceVoterTier.SeniorReviewer]: { one: 'a senior reviewer', several: 'senior reviewers' },
	[PackageEvidence.EvidenceVoterTier.GeneralReviewer]: { one: 'a reviewer', several: 'reviewers' },
	[PackageEvidence.EvidenceVoterTier.GeneralUser]: { one: 'a user', several: 'users' },
	[PackageEvidence.EvidenceVoterTier.Trainee]: { one: 'a trainee', several: 'trainees' },
	[PackageEvidence.EvidenceVoterTier.Pipeline]: { one: 'an extractor', several: 'extractors' },
};

/**
 * A verdict in words, naming who decided it and by what margin, and any more trusted tier whose tie left the decision
 * to them, e.g. "Confirmed by 2 of 3 voting expert reviewers (staff reviewers tied 1–1)". A lone voter reads as
 * "Confirmed by a staff reviewer", and a unanimous tier as "Rejected by both voting reviewers" or "Rejected by all 3
 * voting reviewers".
 *
 * @param confidence - The region's confidence.
 * @returns The wording, or null for a region that is only possible evidence, which gets no badge.
 */
export function describeEvidenceConfidence(confidence: PackageEvidence.EvidenceConfidence): string | null {
	const { verdict, decidedBy, tallies } = confidence;
	const decidingIndex = tallies.findIndex(({ tier }) => tier === decidedBy);
	const tally = tallies[decidingIndex];
	if (verdict === PackageEvidence.EvidenceConfidenceVerdict.Possible || tally === undefined) {
		return null;
	}
	const isConfirmed = verdict === PackageEvidence.EvidenceConfidenceVerdict.Confirmed;
	const agreeing = isConfirmed ? tally.votesFor : tally.votesAgainst;
	const total = tally.votesFor + tally.votesAgainst;
	const { one, several } = TIER_VOTER_NAMES[tally.tier];
	const voters =
		total === 1 ? one
		: agreeing === total && total === 2 ? `both voting ${several}`
		: agreeing === total ? `all ${total} voting ${several}`
		: `${agreeing} of ${total} voting ${several}`;
	// Tallies run from the most trusted tier down, so every tie before the decider is one it overrode.
	const ties = tallies
		.slice(0, decidingIndex)
		.filter(({ votesFor, votesAgainst }) => votesFor === votesAgainst)
		.map(({ tier, votesFor, votesAgainst }) => `${TIER_VOTER_NAMES[tier].several} tied ${votesFor}–${votesAgainst}`);
	const tieNote = ties.length === 0 ? '' : ` (${ties.join('; ')})`;
	return `${isConfirmed ? 'Confirmed' : 'Rejected'} by ${voters}${tieNote}`;
}
